
import { useEffect, useRef, useState } from "react";
import { useNotify } from "@/hooks/useNotify";
import { supabase } from "@/integrations/supabase/client";
import { AuthStateType } from "../types/auth-state";

export function useProfileFetch(authState: AuthStateType) {
  const notify = useNotify();
  const { user, setProfile, setIsLoading } = authState;
  const lastUserIdRef = useRef<string | null>(null);
  const profileFetchedRef = useRef<boolean>(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isAuthStable, setIsAuthStable] = useState(false);
  const authInitializedRef = useRef(false);

  // Debounced profile fetch function
  const debouncedFetchProfile = (userId: string) => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for 300ms debounce
    debounceTimerRef.current = setTimeout(async () => {
      console.log('[Profile] Debounced fetchProfile called with user ID:', userId);
      
      setIsLoading(true);
      
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", userId)
          .single();

        if (error) {
          console.error("[Profile] Error fetching profile:", error);
          
          // If no profile exists, create one
          if (error.code === 'PGRST116') {
            console.log('[Profile] No profile found, creating new one');
            const { data: newProfile, error: createError } = await supabase
              .from("profiles")
              .insert({
                id: userId,
                profile_completed: false,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              })
              .select()
              .single();
            
            if (createError) {
              console.error("[Profile] Error creating profile:", createError);
              notify.error("Profile Creation Failed — Could not create user profile. Please try again.");
              setProfile(undefined);
            } else {
              console.log('[Profile] New profile created:', newProfile);
              setProfile({
                id: newProfile.id || "",
                user_id: newProfile.id || "",
                email: null,
                first_name: null,
                last_name: null,
                personal_phone_number: null,
                profile_photo_url: null,
                company_position: null,
                profile_completed: false,
                company_id: null,
                status: "active",
                suspended_until: null,
                suspended_by: null,
                suspension_reason: null
              });
            }
          } else {
            notify.error(`Failed to load profile — ${error.message}`);
            setProfile(undefined);
          }
        } else {
          console.log('[Profile] Profile data loaded:', data);
          setProfile({
            id: data.id || "",
            user_id: data.id || "",
            email: null, // Email is no longer stored in profiles
            first_name: data.first_name || null,
            last_name: data.last_name || null,
            personal_phone_number: data.personal_phone_number || null,
            profile_photo_url: data.profile_photo_url || null,
            company_position: data.company_position || null,
            profile_completed: data.profile_completed || false,
            company_id: data.company_id || null,
            status: data.status || "active",
            suspended_until: data.suspended_until || null,
            suspended_by: data.suspended_by || null,
            suspension_reason: data.suspension_reason || null
          });
        }
        
        profileFetchedRef.current = true;
        setIsAuthStable(true);
        authInitializedRef.current = true;
      } catch (err) {
        console.error("[Profile] Error in profile fetch:", err);
        notify.error("Authentication Error — Failed to load user profile. Please refresh the page.");
        setProfile(undefined);
        setIsAuthStable(false);
      }

      setIsLoading(false);
    }, 300); // 300ms debounce delay
  };

  useEffect(() => {
    // Skip profile re-fetch during active impersonation to prevent overwriting
    if (authState.isImpersonating) return;
    
    console.log('[Profile] useEffect triggered - user:', !!user, 'user.id:', user?.id);
    
    if (!user?.id) {
      // Only reset state if we've been initialized and this is a real sign-out
      if (authInitializedRef.current) {
        console.log('[Profile] User signed out, resetting profile state');
        setProfile(null);
        lastUserIdRef.current = null;
        profileFetchedRef.current = false;
        setIsAuthStable(false);
        authInitializedRef.current = false;
      } else {
        console.log('[Profile] No user yet, but auth not initialized - waiting for hydration');
      }
      
      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      return;
    }

    // Skip if same user and profile already fetched
    if (lastUserIdRef.current === user.id && profileFetchedRef.current) {
      console.log('[Profile] Same user ID and profile already fetched, skipping');
      setIsAuthStable(true);
      return;
    }

    console.log('[Profile] Starting debounced profile fetch for user:', user.id);
    lastUserIdRef.current = user.id;
    profileFetchedRef.current = false; // Reset fetch flag
    setIsAuthStable(false);
    
    // Use debounced fetch
    debouncedFetchProfile(user.id);

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [user?.id, setProfile, setIsLoading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Force refresh profile method
  const refreshProfile = async () => {
    if (!user?.id) {
      console.log('[Profile] refreshProfile called but no user ID available');
      return;
    }
    
    console.log('[Profile] Force refreshing profile for user:', user.id);
    profileFetchedRef.current = false;
    lastUserIdRef.current = null; // Reset to force fetch
    setIsAuthStable(false);
    debouncedFetchProfile(user.id);
  };

  return {
    isAuthStable,
    refreshProfile
  };
}
